import { useState } from "react";
import { useLocation } from "wouter";
import { VIEW_PATH, pathToViewMode } from "../../shared/types/view-routes";
import type { ViewMode } from "../../shared/types";
import {
  Sun,
  CalendarIcon,
  CheckSquare,
  Flame,
  ListIcon,
  BarChart3,
  ClockIcon,
  CalendarWeekIcon,
  LayoutGridIcon,
  SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Logo,
  GridIcon,
  BookIcon,
  KanbanIcon,
  WrenchIcon,
  BugIcon,
} from "../icons";

interface SidebarProps {
  readonly advancedMode: boolean;
  readonly hiddenSections: ReadonlySet<ViewMode>;
  readonly onSettings: () => void;
  readonly onBugReport: () => void;
  readonly onAdmin?: () => void;
  readonly onDev?: () => void;
  readonly onPinnedChange?: (pinned: boolean) => void;
}

interface IconProps {
  readonly className?: string;
}

interface ItemSection {
  readonly items: Array<{
    readonly mode: ViewMode;
    readonly label: string;
    readonly icon: (props: IconProps) => React.ReactNode;
  }>;
}

const itemSections: ReadonlyArray<ItemSection> = [
  {
    items: [
      { mode: "today", label: "Aujourd'hui", icon: Sun },
      { mode: "tasks", label: "Tâches", icon: CheckSquare },
      { mode: "habits", label: "Habitudes", icon: Flame },
    ],
  },
  {
    items: [
      { mode: "calendar", label: "Calendrier", icon: CalendarIcon },
      { mode: "agenda", label: "Agenda", icon: ListIcon },
      { mode: "weekly", label: "Semaine", icon: CalendarWeekIcon },
      { mode: "schedule", label: "Emploi du temps", icon: LayoutGridIcon },
    ],
  },
  {
    items: [
      { mode: "kanban", label: "Kanban", icon: KanbanIcon },
      { mode: "eisenhower", label: "Eisenhower", icon: GridIcon },
    ],
  },
  {
    items: [
      { mode: "journal", label: "Journal", icon: BookIcon },
      { mode: "stats", label: "Stats", icon: BarChart3 },
      { mode: "free-slots", label: "Créneaux libres", icon: ClockIcon },
    ],
  },
];

function getSidebarPinnedState(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem("palnia_sidebar_expanded");
  return stored === "true";
}

const ADVANCED_MODES = new Set<ViewMode>(["kanban", "eisenhower", "journal"]);

export function Sidebar({ advancedMode, hiddenSections, onSettings, onBugReport, onAdmin, onDev, onPinnedChange }: SidebarProps) {
  const [location, navigate] = useLocation();
  const active = pathToViewMode(location);
  const [isPinned, setIsPinned] = useState<boolean>(getSidebarPinnedState);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const isExpanded = isPinned || isHovered;

  const handleTogglePin = (): void => {
    const newState = !isPinned;
    setIsPinned(newState);
    localStorage.setItem("palnia_sidebar_expanded", String(newState));
    onPinnedChange?.(newState);
  };

  // Collapse on navigate (for touch devices where mouseLeave doesn't fire)
  const handleNavigate = (mode: ViewMode): void => {
    navigate(VIEW_PATH[mode]);
    if (!isPinned) setIsHovered(false);
  };

  return (
    <aside
      className={`hidden md:flex md:flex-col fixed left-0 top-0 h-screen bg-surface border-r border-border transition-all duration-300 ease-out z-40 ${
        isExpanded ? "md:w-42" : "md:w-14"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`flex items-center h-10 border-b border-border ${isExpanded ? "px-3" : "justify-center"}`}>
        <Logo />
        {isExpanded && (
          <span className="flex-1 text-center text-base font-bold text-ink tracking-tight whitespace-nowrap">
            Palnia
          </span>
        )}
      </div>

      <nav className="flex-1 flex flex-col overflow-y-auto">
        {itemSections
          .map((section) => ({
            ...section,
            items: section.items.filter(
              (item) =>
                (advancedMode || !ADVANCED_MODES.has(item.mode)) &&
                !hiddenSections.has(item.mode),
            ),
          }))
          .filter((section) => section.items.length > 0)
          .map((section, sectionIdx, filteredSections) => (
          <div key={sectionIdx} className="flex flex-col">
            <div className="flex flex-col gap-1 p-2">
              {section.items.map(({ mode, label, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => handleNavigate(mode)}
                  className={`flex items-center rounded-lg transition-all gap-3 h-9 ${
                    isExpanded ? "px-3" : "justify-center w-9 mx-auto"
                  } ${
                    active === mode
                      ? "bg-accent/10 text-accent"
                      : "text-ink-muted hover:text-ink hover:bg-surface-alt"
                  }`}
                  title={label}
                >
                  <div className="shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  {isExpanded && (
                    <span className="text-sm font-medium whitespace-nowrap">
                      {label}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {sectionIdx < filteredSections.length - 1 && (
              <div className={`border-t border-border/50 ${!isExpanded ? "mx-1" : "mx-2"}`} />
            )}
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-2 flex flex-col gap-2">
        {import.meta.env.DEV && onDev && (
          <button
            onClick={onDev}
            className={`flex items-center rounded-lg text-amber-500 hover:text-amber-400 hover:bg-surface-alt transition-all gap-3 h-9 ${
              isExpanded ? "px-3 w-full" : "justify-center w-9 mx-auto"
            }`}
            title="Dev Tools"
          >
            <div className="shrink-0">
              <WrenchIcon className="w-4 h-4" />
            </div>
            {isExpanded && (
              <span className="text-sm font-medium whitespace-nowrap">
                Dev
              </span>
            )}
          </button>
        )}
        {onAdmin && (
          <button
            onClick={onAdmin}
            className={`flex items-center rounded-lg text-violet-500 hover:text-violet-400 hover:bg-violet-500/10 transition-all gap-3 h-9 ${
              isExpanded ? "px-3 w-full" : "justify-center w-9 mx-auto"
            }`}
            title="Administration"
          >
            <div className="shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/></svg>
            </div>
            {isExpanded && (
              <span className="text-sm font-medium whitespace-nowrap">
                Admin
              </span>
            )}
          </button>
        )}
        <button
          onClick={onBugReport}
          className={`flex items-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-all gap-3 h-9 ${
            isExpanded ? "px-3 w-full" : "justify-center w-9 mx-auto"
          }`}
          title="Signaler un bug"
        >
          <div className="shrink-0">
            <BugIcon className="w-4 h-4" />
          </div>
          {isExpanded && (
            <span className="text-sm font-medium whitespace-nowrap">
              Signaler
            </span>
          )}
        </button>
        <button
          onClick={onSettings}
          className={`flex items-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-all gap-3 h-9 ${
            isExpanded ? "px-3 w-full" : "justify-center w-9 mx-auto"
          }`}
          title="Paramètres"
        >
          <div className="shrink-0">
            <SettingsIcon className="w-4 h-4" />
          </div>
          {isExpanded && (
            <span className="text-sm font-medium whitespace-nowrap">
              Paramètres
            </span>
          )}
        </button>

        <button
          onClick={handleTogglePin}
          className={`flex items-center justify-center rounded-lg text-ink-muted hover:text-ink hover:bg-surface-alt transition-all h-9 ${
            isExpanded ? "px-3 w-full" : "w-9 mx-auto"
          }`}
          title={isPinned ? "Replier" : "Épingler"}
        >
          {isPinned ? <ChevronLeft /> : <ChevronRight />}
        </button>
      </div>
    </aside>
  );
}
